import { AttendeeInformation } from '../interfaces/interfaces';

export interface CheckoutDelegationPlace {
  nfcCheckoutAuthentication?: boolean | null;
  checkedInCheckOutEnabled?: boolean | null;
  checkOutOnlyEnabled?: boolean | null;
}

export function normalizeAttendeeUpn(upn: string | null | undefined): string {
  return upn?.trim().toLowerCase() ?? '';
}

export function attendeeIdentityKeys(
  attendee: AttendeeInformation | null | undefined,
): string[] {
  return Array.from(
    new Set(
      [attendee?.userPrincipalName, ...(attendee?.userAliases ?? [])]
        .map(normalizeAttendeeUpn)
        .filter(Boolean),
    ),
  );
}

export function isResourceAttendee(
  attendee: AttendeeInformation | null | undefined,
): boolean {
  return attendee?.type?.trim().toLowerCase() === 'resource';
}

export function attendeesShareIdentity(
  left: AttendeeInformation | null | undefined,
  right: AttendeeInformation | null | undefined,
): boolean {
  const leftKeys = new Set(attendeeIdentityKeys(left));
  return attendeeIdentityKeys(right).some((key) => leftKeys.has(key));
}

function mergeAliases(
  left: AttendeeInformation | null | undefined,
  right: AttendeeInformation | null | undefined,
  primaryUpn: string | null | undefined,
): string[] {
  const primaryIdentity = normalizeAttendeeUpn(primaryUpn);
  const aliasesByIdentity = new Map<string, string>();
  [
    left?.userPrincipalName,
    ...(left?.userAliases ?? []),
    right?.userPrincipalName,
    ...(right?.userAliases ?? []),
  ].forEach((alias) => {
    const normalized = normalizeAttendeeUpn(alias);
    if (normalized && normalized !== primaryIdentity) {
      aliasesByIdentity.set(normalized, alias?.trim() ?? '');
    }
  });
  return Array.from(aliasesByIdentity.values());
}

export function deduplicateAttendeesByUpn(
  attendees: AttendeeInformation[] | null | undefined,
): AttendeeInformation[] {
  const deduplicated: AttendeeInformation[] = [];
  (attendees ?? []).forEach((attendee) => {
    if (attendeeIdentityKeys(attendee).length === 0) return;

    const cloned: AttendeeInformation = {
      ...attendee,
      userAliases: [...(attendee.userAliases ?? [])],
      ...(isResourceAttendee(attendee) ? { canCheckout: false } : {}),
    };
    const matchingIndexes = deduplicated
      .map((existing, index) =>
        isResourceAttendee(existing) === isResourceAttendee(cloned) &&
        attendeesShareIdentity(existing, cloned)
          ? index
          : -1,
      )
      .filter((index) => index >= 0);
    if (matchingIndexes.length === 0) {
      deduplicated.push(cloned);
    } else {
      const firstIndex = matchingIndexes[0];
      const aliases = matchingIndexes.reduce(
        (merged, index) => ({
          ...cloned,
          userAliases: mergeAliases(
            merged,
            deduplicated[index],
            cloned.userPrincipalName,
          ),
        }),
        cloned,
      ).userAliases;
      deduplicated[firstIndex] = {
        ...cloned,
        userAliases: aliases,
      };
      matchingIndexes
        .slice(1)
        .sort((left, right) => right - left)
        .forEach((index) => deduplicated.splice(index, 1));
    }
  });
  return deduplicated;
}

export function isCheckoutDelegationConfigured(
  place: CheckoutDelegationPlace | null | undefined,
): boolean {
  return !!(
    place?.nfcCheckoutAuthentication &&
    (place.checkedInCheckOutEnabled || place.checkOutOnlyEnabled)
  );
}

export function getCheckoutDelegates(
  attendees: AttendeeInformation[] | null | undefined,
): AttendeeInformation[] {
  return deduplicateAttendeesByUpn(attendees).filter(
    (attendee) =>
      !isResourceAttendee(attendee) && attendee.canCheckout === true,
  );
}

/**
 * Reconciles the normal participant picker with delegate metadata selected
 * from the same modal.
 * Removing a participant also removes their delegate designation because the
 * attendee is no longer present in the returned list. An unknown canCheckout
 * stays unknown so the backend keeps the saved value.
 */
export function reconcileParticipantSelection(
  previousAttendees: AttendeeInformation[] | null | undefined,
  selectedParticipants: AttendeeInformation[] | null | undefined,
): AttendeeInformation[] {
  void previousAttendees;

  return deduplicateAttendeesByUpn(selectedParticipants);
}

export function mergeIncomingCheckoutMetadata(
  previousAttendees: AttendeeInformation[] | null | undefined,
  incomingAttendees: AttendeeInformation[] | null | undefined,
): AttendeeInformation[] {
  const previous = deduplicateAttendeesByUpn(previousAttendees);

  return deduplicateAttendeesByUpn(incomingAttendees).map((incoming) => {
    if (incoming.canCheckout !== null && incoming.canCheckout !== undefined) {
      return { ...incoming };
    }

    const previousAttendee = previous.find((attendee) =>
      attendeesShareIdentity(attendee, incoming),
    );
    return previousAttendee?.canCheckout === null ||
      previousAttendee?.canCheckout === undefined
      ? { ...incoming }
      : { ...incoming, canCheckout: previousAttendee.canCheckout };
  });
}

/**
 * Prepares attendees to cross the wire. canCheckout is sent as-is: an unknown
 * value tells the backend to keep the saved value.
 */
export function stripCheckoutDelegateUiState(
  attendees: AttendeeInformation[] | null | undefined,
): AttendeeInformation[] {
  return deduplicateAttendeesByUpn(attendees).filter(
    (attendee) => !isResourceAttendee(attendee),
  );
}
